import { Div, FlexCol, FlexRow, P } from "@quarks";
import { useTranslation } from "react-i18next";

import { ExploreState } from "src/components/pages/HereAndNow/useExploreState";
import { HaNExploreListItem } from "./HaNExploreListItem";
import { List, type RowComponentProps } from "react-window";
import { Illustration } from "@atoms";
import { GroupGrid } from "./styles";
import { Resource, resourceGroupOrder } from "@lib/store/src/data/buildingResources";
import { GroupItem } from "./GroupItem";
import { useFeatureToggle } from "@hooks";
import { trackEvent } from "@lib/infrastructure";
import { HaNExploreSearchBar } from "../HaNExploreSearchBar";
import { getIsHMEnabledAndIntegrationEnabled, useAppSelector } from "@lib/store";
type Props = {
  exploreState: ExploreState;
  onClick: (facility: Resource | null) => void;
  handleOnBackExplore: () => void;
};

type RowProps = {
  items: Resource[];
  exploreState: ExploreState;
  onClick: (facility: Resource | null) => void;
};

export const HaNExploreTab = ({ exploreState, onClick, handleOnBackExplore }: Props) => {
  const { t } = useTranslation();

  const {
    buildingId,
    resourceData,
    resourceGroups,
    setRawQuery,
    highlightedResources,
    hasSearchQuery,
    hasFilterSelected,
  } = exploreState;
  const resourcesHaveError = !resourceData || resourceData.status === "failed";
  const resourcesAreLoading = resourceData?.status === "loading";
  const { WorkspacesOnHereAndNow: isWorkspacesOnHereAndNowEnabled } = useFeatureToggle();
  const isMeetingCalendarEnabled = useAppSelector(getIsHMEnabledAndIntegrationEnabled);

  const showResourceGroupList = !hasSearchQuery && !hasFilterSelected && Object.keys(resourceGroups).length > 2;

  return (
    <div className="grid h-full grid-rows-[auto_1fr] overflow-auto">
      <div className="sticky top-0 z-10 bg-white">
        <FlexRow
          height="var(--tabs-height)"
          justifyContent="center"
          alignItems="center">
          <HaNExploreSearchBar
            exploreState={exploreState}
            onBack={handleOnBackExplore}
            showBackButton={!!(isWorkspacesOnHereAndNowEnabled || isMeetingCalendarEnabled)}
          />
        </FlexRow>
        {!showResourceGroupList && (
          <p className="bold py-2 px-5">
            {t("hereAndNow:ResultCount", { count: exploreState.highlightedResources.length })}
          </p>
        )}
      </div>
      <FlexRow
        gap={16}
        className="h-full"
        flex="1">
        {showResourceGroupList ? (
          <FlexCol
            flex="1"
            className="h-full">
            <GroupGrid data-testid="HaNExploreList-Container">
              {resourceGroupOrder.map(resourceGroupKey => {
                const resourceGroup = resourceGroups[resourceGroupKey];
                return resourceGroup ? (
                  <GroupItem
                    key={resourceGroupKey}
                    onClick={() => {
                      setRawQuery(resourceGroup.displayName);

                      trackEvent("HereAndNow_Explore__CategoryClick", { category: resourceGroupKey, buildingId });
                    }}
                    {...resourceGroup}
                  />
                ) : null;
              })}
            </GroupGrid>
          </FlexCol>
        ) : highlightedResources.length ? (
          <List<RowProps>
            rowComponent={RowComponent}
            rowCount={highlightedResources.length}
            rowHeight={76}
            rowProps={{
              items: highlightedResources,
              exploreState,
              onClick: onClick,
            }}
          />
        ) : (
          <FlexCol
            justifyContent="center"
            alignItems="center"
            gap={16}
            padding={32}>
            <Illustration
              size="100px"
              illustration="noResults"
            />
            <P
              color="currentColor"
              textAlign="center">
              {t("hereAndNow:NoSearchResults")}
            </P>
          </FlexCol>
        )}
      </FlexRow>
      {resourcesAreLoading && <p>Loading...</p>}

      {resourcesHaveError && (
        <>
          <p>{exploreState.resourceData?.errorMessage ?? t("ResourcesNotLoaded")}</p>
          <button onClick={exploreState.loadResources}>{t("Retry")}</button>
        </>
      )}
    </div>
  );
};

const RowComponent = ({ index, items, style, onClick, exploreState }: RowComponentProps<RowProps>) => {
  return (
    <Div style={{ ...style, padding: "0 10px" }}>
      <HaNExploreListItem
        exploreState={exploreState}
        resource={items[index]}
        onClick={onClick}
      />
    </Div>
  );
};
